import { catalogItem, MATERIALS } from './catalog.js';

const validFilter = value => MATERIALS.includes(value) ? value : 'all';
export function catalogHref(kind = null, material = 'all') {
  if (kind && catalogItem(kind)) return `/catalog/${encodeURIComponent(kind)}/`;
  const filter = validFilter(material);
  return `/catalog/${filter === 'all' ? '' : `?material=${filter}`}`;
}
export function parseRoute(value) {
  const [pathname, query = ''] = value.replace(/^#/, '').split('?');
  const path = pathname.replace(/\/$/, '');
  const filter = validFilter(new URLSearchParams(query).get('material'));
  if (path === '/catalog' || path?.startsWith('/catalog/')) {
    let kind;
    try { kind = decodeURIComponent(path.slice('/catalog/'.length)); } catch { /* Invalid links open the catalog. */ }
    return catalogItem(kind) ? {screen:'play',kind} : {screen:'catalog',filter};
  }
  if (path === '/collection') return {screen:'collection'};
  if (path === '/pile') return {screen:'pile'};
  if (path?.startsWith('/collection/')) {
    try { return {screen:'play',id:decodeURIComponent(path.slice('/collection/'.length))}; }
    catch { return {screen:'collection'}; }
  }
  return {screen:'gacha'};
}
function hrefFor(route) {
  if (route.kind) return catalogHref(route.kind);
  if (route.id) return `/#/collection/${encodeURIComponent(route.id)}`;
  if (route.screen === 'catalog') return catalogHref(null, route.filter);
  if (route.screen === 'pile') return '/#/pile';
  return route.screen === 'collection' ? '/#/collection' : '/';
}

// History stores only navigation and scroll. Owned toys and wax damage stay in ToyStore.
export class Navigation {
  constructor({window,store,render,getScroll,restoreScroll}) {
    Object.assign(this,{window,store,render,getScroll,restoreScroll});
    this.scrollPositions = new Map();
    this.onHistory = () => this.restore();
    this.onScroll = () => {
      if (this.currentURL !== this.window.location.href) return;
      this.scrollPositions.set(this.currentKey,this.getScroll());
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(()=>this.capture(),200);
    };
  }
  start() {
    this.window.history.scrollRestoration = 'manual';
    this.window.addEventListener('popstate',this.onHistory);
    this.window.addEventListener('hashchange',this.onHistory);
    this.window.addEventListener('scroll',this.onScroll,true);
    this.restore();
  }
  capture() {
    clearTimeout(this.scrollTimer);
    const history = this.window.history;
    history.replaceState({...history.state,mofu:{...history.state?.mofu,scroll:this.getScroll()}},'',this.window.location.href);
  }
  normalize(route) {
    if (route.screen === 'pile' && !this.store.state.collection.length) return {screen:'collection'};
    if (route.id && !this.store.state.collection.some(toy=>toy.id===route.id)) return {screen:'collection'};
    return route;
  }
  apply(route) {
    const state = this.store.state;
    // Leaving an animation must not strand the saved capsule in a rolling phase.
    this.store.seal();
    if (route.kind) this.store.preview(route.kind);
    else if (route.id) this.store.select(route.id);
    else {
      Object.assign(state,{screen:route.screen,previewKind:null,newToy:false});
      if (route.screen === 'catalog') state.catalogFilter = route.filter;
      if (route.screen === 'gacha') state.phase = state.pending ? 'sealed' : 'idle';
    }
  }
  restore() {
    const location = this.window.location;
    const route = this.normalize(parseRoute(location.hash.startsWith('#/') ? location.hash : location.pathname + location.search));
    const metadata = {...this.window.history.state?.mofu};
    metadata.key ??= `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    this.window.history.replaceState({...this.window.history.state,mofu:metadata},'',hrefFor(route));
    // Canonicalize old links before deduplicating paired browser events.
    if (this.window.location.href === this.currentURL && metadata.key === this.currentKey) return;
    clearTimeout(this.scrollTimer);
    this.currentURL = this.window.location.href;
    this.currentKey = metadata.key;
    this.apply(route);
    this.render();
    this.restoreScroll(this.scrollPositions.get(this.currentKey) ?? (Number.isFinite(metadata.scroll) ? metadata.scroll : 0));
  }
  navigate(name,payload) {
    const state = this.store.state;
    let route;
    if (name === 'catalog-back') {
      if (this.window.history.state?.mofu?.fromCatalog) {
        this.window.history.back();
        return true;
      }
      route = {screen:'catalog',filter:state.catalogFilter};
    } else if (name === 'catalog') route = {screen:'catalog',filter:state.catalogFilter};
    else if (name === 'filter') route = {screen:'catalog',filter:validFilter(payload)};
    else if (['gacha','collection','pile'].includes(name)) route = {screen:name};
    else if (name === 'try') route = catalogItem(payload) ? {screen:'play',kind:payload} : {screen:'catalog',filter:state.catalogFilter};
    else if (name === 'select' || name === 'play') route = {screen:'play',id:name === 'select' ? payload : state.selectedId};
    else return false;
    if (route.screen === 'play' && !route.id && !route.kind) route = {screen:'collection'};
    route = this.normalize(route);
    const href = hrefFor(route);
    if (new URL(href, this.window.location.href).href === this.window.location.href) {
      if (name === 'gacha' && state.phase === 'revealed') { this.apply(route); this.render(); }
      return true;
    }
    this.capture();
    const fromCatalog = state.screen === 'catalog' && Boolean(route.kind);
    this.window.history.pushState({mofu:{scroll:0,fromCatalog}},'',href);
    this.restore();
    return true;
  }
}
